// Tag-select card for the calendar sidebar "phone" preview (formType 'TagSelect').
// Copy of the workouts module's TagCard rendering the full UI; the selected exercise name
// comes from the workout object. Interaction is blocked by the overlay.
import React from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { BoxShadow } from '../../../../shared/GlobalStyles';

const Container = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 170px;
  align-items: center;
  box-shadow: ${BoxShadow};
  border-radius: 12px;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.surface};
  padding: 12px;
`;

const CompleteButton = styled('button')`
  width: 140px;
  height: 37px;
  border-radius: 40px;
  border: ${(props) => (props.isWorkoutComplete ? '1px solid #00b371;' : `1px solid ${props.theme.colors.borderLight}`)};
  margin-top: 20px;
  background: ${(props) => (props.isWorkoutComplete ? '#00b371;' : props.theme.colors.surface)};
`;

const HeaderText = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  margin-bottom: 15px;
  font-family: 'Nunito Sans';
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InnerCompleteButton = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CompleteButtonText = styled('div')`
  font-size: 12px;
  margin-left: 10px;
  font-weight: 600;
  font-family: 'Nunito Sans';
  color: ${(props) => (props.isWorkoutComplete ? 'white' : props.color)};
`;

const TriggerAndIcon = styled('div')`
  width: 90%;
  display: flex;
  border-radius: 4px;
  border: 1px solid #d8d8d8;
  align-items: center;
`;

const HeaderDivider = styled('div')`
  border: 0.5px solid ${({ theme }) => theme.colors.border};
  height: 60%;
`;

const IconContainer = styled('div')`
  width: 30px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TagSelectTrigger = styled('div')`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.surface};
  user-select: none;
  border-radius: 4px;
  min-height: 38px;
  padding: 8px;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  position: relative;
  border: none;
  flex: 1;
`;

const CoachCompletionText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
`;

const CalendarTagCard = ({ workoutObject }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const theme = useTheme();
  const { colors } = theme;

  const selectedName = workoutObject?.exercise?.name || '';
  const showConfirm = !workoutObject.coachCompletionOnly
    || (workoutObject.coachCompletionOnly && currentUser.admin);

  return (
    <Container>
      <HeaderText>Select an exercise and click &apos;Confirm&apos;</HeaderText>
      <TriggerAndIcon>
        <TagSelectTrigger>{selectedName}</TagSelectTrigger>
        <HeaderDivider />
        <IconContainer>
          <IcomoonReact iconSet={iconSet} size={15} icon='down-arrow' color={colors.textTertiary} />
        </IconContainer>
      </TriggerAndIcon>
      {showConfirm ? (
        <CompleteButton type='button' isWorkoutComplete={workoutObject.fullyCompleted}>
          <InnerCompleteButton>
            <IcomoonReact
              iconSet={iconSet}
              size={15}
              icon='checkmark'
              color={!workoutObject.fullyCompleted ? colors.text : 'white'}
            />
            <CompleteButtonText
              isWorkoutComplete={workoutObject.fullyCompleted}
              color={theme.colors.text}
            >
              Confirm
            </CompleteButtonText>
          </InnerCompleteButton>
        </CompleteButton>
      ) : null}
      {workoutObject.coachCompletionOnly && !currentUser.admin ? (
        <CompleteButton type='button' title='Coach Input' />
      ) : null}
      {workoutObject.coachCompletionOnly ? (
        <CoachCompletionText>A coach must complete this workout item</CoachCompletionText>
      ) : null}
    </Container>
  );
};

CalendarTagCard.propTypes = {
  workoutObject: PropTypes.instanceOf(Object).isRequired,
};

export default CalendarTagCard;
